import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { SuiToolbarComponent } from '@sensika/sui/toolbar';

/** One tab of the toolbar's strip (pinned view presets, lifecycle filters, ...). */
export interface GridToolbarTab {
  id: string;
  label: string;
  /** Renders an in-place remove control that emits `tabRemove` (pinned presets). */
  removable?: boolean;
}

/**
 * The one grid toolbar: title + icon, a tab strip, count + paginator, and the
 * shared controls Refresh / Reset Filters / Export - each shown only when its
 * `show*` input is set and reported through an output. The view contributes
 * its own controls through three content slots, all rendered inside the same
 * `.table-header` recipe (`layers/_admin.scss`) so every grid reads the same:
 *
 * - `[toolbarIcon]`     - a custom title icon (connector logos) before the text
 * - `[toolbarStatus]`   - after the count/paginator (selection pills, ...)
 * - `[toolbarActions]`  - the view's own create / import / bulk buttons
 * - `[toolbarViewOptions]` - after the shared controls; the base grid projects
 *   its view-preset controls here, standalone grids project nothing.
 *
 * Presentational only: no grid, query or preset knowledge, which is what lets
 * the standalone grids that cannot compose `app-data-grid` use it too.
 */
@Component({
  selector: 'app-grid-toolbar',
  templateUrl: './grid-toolbar.component.html',
  styleUrl: './grid-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GridToolbarComponent {
  protected readonly faSpinner = faSpinner;
  /**
   * False hides the whole control strip (count, paginator, actions, view
   * options) while the host's first load is in flight, so a view arrives with
   * its controls in their final state instead of settling in front of the user.
   */
  readonly ready = input<boolean>(true);
  readonly title = input<string>('');
  readonly icon = input<IconProp | null>(null);

  readonly tabs = input<GridToolbarTab[]>([]);
  readonly activeTabId = input<string | null>(null);
  /** Accessible name of the tab list. */
  readonly tabsLabel = input<string>('');
  readonly tabRemoveLabel = input<string>('');
  readonly tabChange = output<string>();
  readonly tabRemove = output<string>();

  /** Null hides the counter altogether. */
  readonly count = input<number | null>(null);
  readonly loading = input<boolean>(false);

  readonly collectionSize = input<number>(0);
  readonly currentPage = input<number>(1);
  readonly pageSize = input<number>(0);
  readonly pageChange = output<number>();

  readonly showRefresh = input<boolean>(false);
  readonly showResetFilters = input<boolean>(false);
  readonly showExportCsv = input<boolean>(false);
  readonly showExportCsvServerSide = input<boolean>(false);
  private readonly strip = viewChild(SuiToolbarComponent);
  /**
   * True while the control strip no longer fits and has collapsed its labelled
   * buttons to icons (sui-toolbar's ResizeObserver). Consumers mirror it on the
   * controls they project: `[iconOnly]="toolbar.collapsed()"`.
   */
  readonly collapsed = computed(() => this.strip()?.collapsed() ?? false);

  readonly refresh = output<void>();
  readonly resetFilters = output<void>();
  readonly exportCsv = output<void>();
  readonly exportCsvServerSide = output<void>();

  readonly showPagination = computed(
    () => this.pageSize() > 0 && this.collectionSize() > this.pageSize(),
  );
  readonly showExport = computed(
    () => this.showExportCsv() || this.showExportCsvServerSide(),
  );
}
